import {
    FEVER_API_ENDPOINT,
    FEVER_LOGIN_API_ENDPOINT,
    THIRD_PARTY_AUTH_ENDPOINT,
    ZINES_API_ENDPOINT
} from 'config/envVariable';
import { cacheBootPromoInfo } from 'api/client';
import { createCSP } from 'utils/createElement';
import prefetchLotteryGameHtml from 'client/utils/prefetchLotteryGameHtml';

const clientFlowRegex = /^\/promotions\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\/\d+\/(join|result|form|lottery|prize\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})+/;

const FEVER_CDN_DOMAIN = [
    'https://utility.fevercdn.com',
    'https://widget.fevercdn.com',
    'https://assets.fevercdn.com',
    'https://image.fevercdn.com',
    'https://file.fevercdn.com', // file-tool（Media Manager）上傳圖片 (prod)
    'https://file-dev.fevercdn.com' // file-tool（Media Manager）上傳圖片 (dev)
];

// 使用者上傳素材的 S3 bucket ，gif / svg / mp4 不走 image-proxy 會直接載入原檔。
// 只放進 img-src / media-src，不放 script-src 等，避免上傳檔被當成腳本執行。
const FEVER_UPLOAD_BUCKET_DOMAIN = [
    'https://picture-original.fevercdn.com',
    'https://picture-thumb.fevercdn.com',
    'https://video-original.fevercdn.com'
];

const DEFAULT_SCRIPT_SRC = [
    `'self'`,
    `'unsafe-eval'`,
    'https://www.googleadservices.com', // GTM
    'https://www.google.com', // GTM
    'https://googleads.g.doubleclick.net', // GTM
    'https://googletagmanager.com', // GTM
    'https://tagmanager.google.com', // GTM
    'https://*.googletagmanager.com', // GA4
    'https://cse.google.com', // Google Custom Search Engine (CSE)
    'https://connect.facebook.net', // Facebook 追蹤碼
    'https://d.line-scdn.net', // Line 追蹤碼
    'https://cdnjs.cloudflare.com', // Cloudflare CDN (Tone.js, slick-carousel)
    'https://challenges.cloudflare.com', // Cloudflare Turnstile
    'https://www.youtube.com', // YouTube iframe API (ReactPlayer)
    ...FEVER_CDN_DOMAIN
].join(' ');

const DEFAULT_STYLE_SRC = [
    `'self'`,
    `'unsafe-inline'`,
    'https://cdnjs.cloudflare.com', // slick-carousel lib
    'https://googletagmanager.com', // GTM
    'https://tagmanager.google.com', // GTM
    'https://fonts.googleapis.com', // Google Fonts CSS（GTM、@fever/ui Material Symbols）
    'https://*.googletagmanager.com', // GTM
    'https://www.google.com', // Google Custom Search Engine (CSE)
    ...FEVER_CDN_DOMAIN
].join(' ');

const DEFAULT_FRAME_SRC = [
    `'self'`,
    'blob:',
    'https://www.googletagmanager.com', // GTM
    'https://googletagmanager.com', // GTM
    'https://challenges.cloudflare.com', // Cloudflare Turnstile
    'https://www.google.com', // Google CSE
    'https://cse.google.com', // Google Custom Search Engine
    'https://www.youtube.com', // YouTube 影片
    'https://www.youtube-nocookie.com', // YouTube 影片（nocookie）
    'https://www.facebook.com', // Facebook
    'https://td.doubleclick.net', // Google Ads
    'https://dev-game-proxy.fevercdn.com', // Game Proxy (dev)
    'https://game-proxy.fevercdn.com', // Game Proxy (prod)
    ...FEVER_CDN_DOMAIN
].join(' ');

const DEFAULT_CONNECT_SRC = [
    `'self'`,
    'data:', // 流程圖片上傳
    'https://*.google-analytics.com', // GA4
    'https://*.analytics.google.com', // GA4
    'https://*.googletagmanager.com', // GA4
    'www.googletagmanager.com', // GTM
    'https://analytics.google.com', // GA4
    'https://www.google.com.tw', // GA4
    'https://www.google.com', // GTM、GA4、Google Ads
    'https://*.g.doubleclick.net', // GA4、Google Ads（含 googleads.g.doubleclick.net）
    'https://ad.doubleclick.net', // Google Ads
    'https://www.googleadservices.com', // Google Ads 轉換
    'https://pagead2.googlesyndication.com', // GA4、Google Ads 再行銷
    'https://www.facebook.com', // Facebook Pixel
    'https://identitytoolkit.googleapis.com', // Firebase
    'https://securetoken.googleapis.com', // Firebase
    'https://firestore.googleapis.com', // Firebase Firestore
    'https://o512271.ingest.sentry.io', // Sentry
    'https://cdnjs.cloudflare.com', // Cloudflare CDN (Tone.js, slick-carousel)
    'https://challenges.cloudflare.com', // Cloudflare Turnstile
    'https://messaging.fevercdn.com', // Widget SDK
    'https://messaging-dev.fevercdn.com', // Widget SDK (dev)
    'https://onsite-statistics.fevercdn.com', // Widget SDK statistics
    'https://onsite-statistics-dev.fevercdn.com', // Widget SDK statistics (dev)
    FEVER_API_ENDPOINT,
    ZINES_API_ENDPOINT,
    FEVER_LOGIN_API_ENDPOINT,
    THIRD_PARTY_AUTH_ENDPOINT,
    ...FEVER_CDN_DOMAIN
].join(' ');

const DEFAULT_IMG_SRC = [
    `'self'`,
    'blob:',
    'data:',
    'https://i.ytimg.com', // YouTube 縮圖
    'https://www.google.com', // GA4、Google Ads
    'https://www.google.com.tw', // GA4
    'https://www.facebook.com', // Facebook Pixel
    'https://*.googletagmanager.com', // GTM
    'https://*.google-analytics.com', // GA4
    'https://*.g.doubleclick.net', // GA4、Google Ads（含 googleads.g.doubleclick.net）
    'https://www.googleadservices.com', // Google Ads 轉換
    'https://pagead2.googlesyndication.com', // Google Ads 再行銷
    'https://tr.line.me', // LINE Tag（lt.js 以 <img> 送 tag.gif / err.gif）
    'https://graph.facebook.com', // FB 登入者頭像（getAvatarFromEntryData）
    'https://*.fbsbx.com', // FB 頭像 302 轉址目標（platform-lookaside.fbsbx.com）
    'https://*.fbcdn.net', // FB 頭像 302 轉址目標（scontent-*.fbcdn.net）
    'https://profile.line-scdn.net', // LINE 登入者頭像
    ...FEVER_CDN_DOMAIN,
    ...FEVER_UPLOAD_BUCKET_DOMAIN
].join(' ');

const DEFAULT_FONT_SRC = [
    `'self'`,
    'data:',
    'https://fonts.gstatic.com', // Google Fonts 字型檔（含 @fever/ui Icon fallback 的 Material Symbols）
    'https://cdnjs.cloudflare.com', // slick-carousel lib (slick-theme 字型)
    ...FEVER_CDN_DOMAIN // Lato 字型、fevericon
].join(' ');

const DEFAULT_MEDIA_SRC = [
    `'self'`,
    'data:',
    'blob:',
    ...FEVER_CDN_DOMAIN,
    ...FEVER_UPLOAD_BUCKET_DOMAIN
].join(' ');

export default async function generateCSP() {
    const pathName = window.location.pathname;
    const match = pathName.match(clientFlowRegex);

    if (match && match[1]) {
        const promoUuid = match[1];

        try {
            const promoInfo = await cacheBootPromoInfo({ promoUUID: promoUuid });

            prefetchLotteryGameHtml(promoInfo);

            const {
                promotion: {
                    sponsorSetting: { cspSrcDomains = '', enableCSP: sponsorEnableCSP }
                }
            } = promoInfo;

            if (Boolean(sponsorEnableCSP)) {
                // 產生 Nonce
                const nonce = generateNonce();
                window.nonceForCSP = nonce;
                // 預設拒絕所有未明確定義的資源
                const ORIGIN_DEFAULT_SRC = `default-src 'none';`;
                const ORIGIN_SCRIPT_SRC = `script-src 'nonce-${nonce}' ${DEFAULT_SCRIPT_SRC} ${cspSrcDomains};`;
                const ORIGIN_STYLE_SRC = `style-src ${DEFAULT_STYLE_SRC} ${cspSrcDomains};`;
                const ORIGIN_IMG_SRC = `img-src ${DEFAULT_IMG_SRC} ${cspSrcDomains};`;
                const ORIGIN_FONT_SRC = `font-src ${DEFAULT_FONT_SRC} ${cspSrcDomains};`;
                const ORIGIN_MEDIA_SRC = `media-src ${DEFAULT_MEDIA_SRC} ${cspSrcDomains};`;
                const ORIGIN_CONNECT_SRC = `connect-src ${DEFAULT_CONNECT_SRC} ${cspSrcDomains};`;
                const ORIGIN_FRAME_SRC = `frame-src ${DEFAULT_FRAME_SRC} ${cspSrcDomains};`;
                const ORIGIN_WORKER_SRC = `worker-src 'self' blob:;`;
                // Safari < 15.5 不支援 worker-src，Worker 會改看 child-src；未定義則 fallback 到 default-src 'none' 被擋
                // （heic2any、qr-scanner 會用到 Worker）。新版瀏覽器有 frame-src / worker-src 時會忽略 child-src
                const ORIGIN_CHILD_SRC = `child-src 'self' blob:;`;
                const ORIGIN_FORM_ACTION = `form-action 'self' ${cspSrcDomains};`;
                const ORIGIN_OBJECT_SRC = `object-src 'none';`;
                const ORIGIN_BASE_URI = `base-uri 'none';`;
                const ORIGIN_MANIFEST_SRC = `manifest-src 'self';`;
                const [
                    DEFAULT_SRC_DIRECTIVE,
                    SCRIPT_SRC,
                    STYLE_SRC,
                    IMG_SRC,
                    FONT_SRC,
                    MEDIA_SRC,
                    CONNECT_SRC,
                    FRAME_SRC,
                    WORKER_SRC,
                    CHILD_SRC,
                    FORM_ACTION,
                    BASE_URI,
                    OBJECT_SRC,
                    MANIFEST_SRC
                ] = [
                    ORIGIN_DEFAULT_SRC,
                    ORIGIN_SCRIPT_SRC,
                    ORIGIN_STYLE_SRC,
                    ORIGIN_IMG_SRC,
                    ORIGIN_FONT_SRC,
                    ORIGIN_MEDIA_SRC,
                    ORIGIN_CONNECT_SRC,
                    ORIGIN_FRAME_SRC,
                    ORIGIN_WORKER_SRC,
                    ORIGIN_CHILD_SRC,
                    ORIGIN_FORM_ACTION,
                    ORIGIN_BASE_URI,
                    ORIGIN_OBJECT_SRC,
                    ORIGIN_MANIFEST_SRC
                ].map(config => filterSameDomain(config));
                const cspConfig = `${DEFAULT_SRC_DIRECTIVE} ${SCRIPT_SRC} ${STYLE_SRC} ${WORKER_SRC} ${CHILD_SRC} ${FRAME_SRC} ${IMG_SRC} ${CONNECT_SRC} ${OBJECT_SRC} ${BASE_URI} ${FORM_ACTION} ${FONT_SRC} ${MEDIA_SRC} ${MANIFEST_SRC}`;

                createCSP(cspConfig);
                return nonce;
            }
        } catch (err) {
            window.location.href = window.location.origin + '/notfound';
        }
    }
}

/**
 *  產生 Nonce
 */
const generateNonce = () => {
    if (typeof window?.crypto?.getRandomValues === 'function') {
        // Method One：crypto.getRandomValues
        const array = new Uint32Array(2);
        window.crypto.getRandomValues(array);
        return array[0].toString(36) + array[1].toString(36);
    } else if (typeof window?.btoa === 'function') {
        // Method Two：Base64
        // 弱掃誤判：本函式的主要路徑用的是 CSPRNG（上方 window.crypto.getRandomValues），
        // 這行是 crypto.getRandomValues 不存在時的 fallback——browserslist 內的瀏覽器都支援，
        // 實務上不會走到。
        // 且此 nonce 生成後即寫入 window.nonceForCSP 供頁面內合法 inline script 使用，
        // 對同頁腳本本來就不是秘密，攻擊面不在於猜測其熵。
        // nosemgrep: nodejs_scan.javascript-crypto-rule-node_insecure_random_generator
        return window?.btoa(Date.now().toString(36) + Math.random().toString(36).substring(2));
    } else {
        // Method Three：Radom and Date mixed to base36 string
        // 弱掃誤判：本函式的主要路徑用的是 CSPRNG（上方 window.crypto.getRandomValues），
        // 這行是連 crypto 與 btoa 都不存在時的最後 fallback——browserslist 內的瀏覽器都支援
        // crypto.getRandomValues，實務上不會走到。
        // 且此 nonce 生成後即寫入 window.nonceForCSP 供頁面內合法 inline script 使用，
        // 對同頁腳本本來就不是秘密，攻擊面不在於猜測其熵。
        // nosemgrep: nodejs_scan.javascript-crypto-rule-node_insecure_random_generator
        return Math.random().toString(36).substring(2) + Date.now().toString(36);
    }
};

/**
 *  過濾相同 Domain
 */
const filterSameDomain = (str = '') => {
    const parts = str.split(' ');
    const uniqueParts = [...new Set(parts)];
    return uniqueParts.join(' ');
};

export const isClientFlow = (pathName = '') => {
    return Boolean(pathName.match(clientFlowRegex));
};
